import { capitalize, max, min, sum } from 'lodash';
import { FinanceType } from '@upstart/harmony/lib/constants';
import {
  LeaseOffer,
  PurchaseOffer,
  Offer,
} from '@upstart/harmony/lib/legos/paymentTypes';
import { fmtMoney, fmtPercent } from '@upstart/harmony/lib/formatter';
import { isIncentiveProgramExpired } from '@upstart/harmony/lib/incentive-utils';
import {
  Car,
  IncentivesProgram,
  IncentivesProgramOffer,
} from '@upstart/harmony/lib/legos/carTypes';
import { Deal } from '@upstart/harmony/lib/legos/dealTypes';
import { Dealer } from '@upstart/harmony/lib/legos/dealerTypes';
import Modal from 'react-modal';
// @ts-ignore
import Toggle from 'react-bootstrap-toggle';
import React, { ReactNode, useState } from 'react';
import { Action, ModalType } from 'util/amplitudeEvents';
import DataTable from '../../DataTable';
import { formatRate } from '../../../util/format';
import {
  RATE_FACTOR,
  modalOverlayBackgroundColor,
} from '../../../util/constants';
import ProgramDetailsModal from './ProgramDetailsModal';
import Input from '../Input';
import { useIncentives } from './hooks/useIncentives';
import ApplyIncentivesMultipleTerms from './ApplyIncentivesMultipleTerms';
import { isIncentiveApplicable, OfferIncentivesPair } from './offer-utils';
import { filterAndJoinStrings } from '../../../util/utils';
import './EditIncentives.scss';

type EditIncentivesProps = {
  financeType: FinanceType;
  offers: Offer[];
  activeOffer: Offer;
  handleClose(): void;
  onSave(condensedOfferPairs: CondensedOfferPair[]): void;
  deal: Deal;
  dealer: Dealer;
  car: Car;
  isOnePay: boolean;
  sendAmplitudeEvent?: (events: ModalType, action: Action) => void;
  disabled?: boolean;
};

type CondensedOfferPair = {
  offer: Offer;
  consolidated: {
    to?: number;
    from?: number;
    cash: number;
    dealerCash: number;
    apr?: number;
    programs: IncentivesProgram[];
  };
};

export const scrollableModalStyles = {
  overlay: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    background: modalOverlayBackgroundColor,
  },
  content: {
    top: 'unset',
    bottom: 'unset',
    left: 'unset',
    right: 'unset',
    minWidth: '70vw',
    maxWidth: '100vw',
    maxHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
  },
} as const;

export const createOemOfferFromIncentiveOffers = (
  incentiveOffers: IncentivesProgramOffer[],
  currentOffer?: Offer,
) => {
  const filteredIncentiveOffers = incentiveOffers.filter(
    i => !currentOffer?.programs?.find(p => isProgramSame(i.program, p)),
  );

  const offer: {
    to?: number;
    from?: number;
    cash: number;
    dealerCash: number;
    apr?: number;
    programs: IncentivesProgram[];
  } = {
    cash:
      sum(filteredIncentiveOffers.map(o => o.cash || 0)) +
        (currentOffer?.rebate || 0) || 0,
    dealerCash:
      sum(filteredIncentiveOffers.map(o => o.dealerCash || 0)) +
        (currentOffer?.dealerCash || 0) || 0,
    programs: filteredIncentiveOffers
      .map(o => o.program)
      .concat(currentOffer?.programs || []),
  };
  const to = min(incentiveOffers.map(o => o.to));
  if (to) {
    offer.to = to;
  }
  const from = max(incentiveOffers.map(o => o.from));
  if (from) {
    offer.from = from;
  }
  // If the current offer has programs that change APR, then we want to take the minimum of the current offer and the new program offers
  const shouldUseCurrentOfferAPR = currentOffer?.programs?.some(
    p => !!p.appliedApr,
  );
  const currentApr = shouldUseCurrentOfferAPR
    ? (currentOffer as PurchaseOffer)?.apr ||
      ((currentOffer as LeaseOffer)?.rate || 0) / RATE_FACTOR
    : Infinity;

  const apr = min([
    ...filteredIncentiveOffers.map(o => o.apr),
    currentApr || Infinity,
  ]);
  if (apr !== undefined && apr !== Infinity) {
    offer.apr = apr;
  }
  return offer;
};
const isProgramSame = (p1: IncentivesProgram, p2: IncentivesProgram) =>
  p1.appliedApr === p2.appliedApr &&
  p1.appliedCash === p2.appliedCash &&
  p1.appliedDealerCash === p2.appliedDealerCash &&
  p1.id === p2.id &&
  p1.groupId === p2.groupId;
const isAlreadySelected = (
  incentiveOffer: IncentivesProgramOffer,
  activeOffer: Offer,
) =>
  (!incentiveOffer.from ||
    (activeOffer as PurchaseOffer).months >= incentiveOffer.from) &&
  (!incentiveOffer.to ||
    (activeOffer as PurchaseOffer).months <= incentiveOffer.to) &&
  (activeOffer.programs || []).find(program =>
    isProgramSame(program, incentiveOffer.program),
  ) !== undefined;

const areOffersSame = (
  incentiveOffer: IncentivesProgramOffer,
  incentiveOther: IncentivesProgramOffer,
) =>
  incentiveOther.to === incentiveOffer.to &&
  incentiveOther.from === incentiveOffer.from &&
  isProgramSame(incentiveOffer.program, incentiveOther.program);

function EditIncentivesForm({
  financeType,
  activeOffer,
  handleClose,
  onSave,
  offers,
  car,
  deal,
  dealer,
  isOnePay,
  sendAmplitudeEvent,
  disabled = false,
}: EditIncentivesProps) {
  const [showHiddenIncentives, setShowHiddenIncentives] = useState(false);
  const incentiveOffers = useIncentives({
    showHiddenIncentives,
    car,
    financeType,
    deal,
    dealer,
    isOnePay,
    activeOffer: activeOffer as LeaseOffer,
  });

  const [selectedIncentiveOffers, setSelectedIncentiveOffers] = useState<
    IncentivesProgramOffer[]
  >(() => incentiveOffers.filter(o => isAlreadySelected(o, activeOffer)));

  const [applyMore, setApplyMore] = useState(false);

  const selectOffer = (incentiveOffer: IncentivesProgramOffer) => {
    const currentlySelectedOffer = selectedIncentiveOffers.find(
      selectedIncentiveOffer =>
        areOffersSame(incentiveOffer, selectedIncentiveOffer),
    );
    if (currentlySelectedOffer) {
      // unselect the offer if it's already selected
      setSelectedIncentiveOffers(
        selectedIncentiveOffers.filter(
          selectedIncentiveOffer =>
            selectedIncentiveOffer !== currentlySelectedOffer,
        ),
      );
    } else {
      setSelectedIncentiveOffers(
        [...selectedIncentiveOffers, incentiveOffer].sort((a, b) =>
          a.id > b.id ? 1 : -1,
        ),
      );
    }
  };

  const isStackableWithSelected = (incentiveOffer: IncentivesProgramOffer) =>
    selectedIncentiveOffers.find(
      selectedIncentiveOffer =>
        !selectedIncentiveOffer.stackablePrograms.includes(incentiveOffer.id),
    ) === undefined;

  const getHeaders = () => {
    const headers = ['', 'Minimum Term', 'Maximum Term'];
    if (financeType === FinanceType.PURCHASE) {
      headers.push('APR');
    } else if (financeType === FinanceType.LEASE) {
      headers.push('Rate');
    }
    headers.push(
      'Rebate',
      'Dealer Cash',
      'Program',
      'Group Affiliation',
      'Previous Ownership',
    );
    return headers;
  };

  const onSaveClicked = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    event.preventDefault();
    onSave([
      {
        consolidated: createOemOfferFromIncentiveOffers(
          selectedIncentiveOffers,
        ),
        offer: activeOffer,
      },
    ]);
    if (sendAmplitudeEvent) {
      sendAmplitudeEvent(ModalType.INCENTIVES, Action.FINISH);
    }
    handleClose();
  };

  const handleMultipleTermIncentives = (pairs: OfferIncentivesPair[]) => {
    onSave(
      pairs.map(({ offer, incentives }) => ({
        consolidated: createOemOfferFromIncentiveOffers(incentives, offer),
        offer,
      })),
    );
    if (sendAmplitudeEvent) {
      sendAmplitudeEvent(ModalType.INCENTIVES, Action.FINISH);
    }
    handleClose();
  };

  const renderIncentivesSelector = (incentiveOffer: IncentivesProgramOffer) => {
    const checked = !!selectedIncentiveOffers.find(i =>
      areOffersSame(incentiveOffer, i),
    );
    const stackable = checked || isStackableWithSelected(incentiveOffer);
    const isApplicable = isIncentiveApplicable(incentiveOffer, activeOffer);
    const isExpired = isIncentiveProgramExpired(incentiveOffer.program);

    if (checked || (stackable && isApplicable && !isExpired)) {
      return (
        <Input
          onChange={() => selectOffer(incentiveOffer)}
          checked={checked}
          type="checkbox"
          disabled={disabled}
        />
      );
    }
    if (!isApplicable) {
      return (
        <div className="incentive-editor-warning-message">
          {'Not Applicable'}
        </div>
      );
    }
    if (isExpired) {
      return (
        <div className="incentive-editor-warning-message">{'Expired'}</div>
      );
    }
    return (
      <div className="incentive-editor-warning-message">
        {"Can't stack with selected"}
      </div>
    );
  };
  const renderRow = (incentiveOffer: IncentivesProgramOffer) => {
    const row: ReactNode[] = [
      <div className="incentive-editor-selector">
        {renderIncentivesSelector(incentiveOffer)}
      </div>,
      incentiveOffer.from,
      incentiveOffer.to,
    ];
    if (financeType === FinanceType.PURCHASE) {
      const apr =
        incentiveOffer.apr !== undefined
          ? fmtPercent(incentiveOffer.apr)
          : undefined;
      row.push(apr);
    } else if (financeType === FinanceType.LEASE) {
      const rate =
        incentiveOffer.apr !== undefined
          ? formatRate(incentiveOffer.apr * RATE_FACTOR)
          : undefined;
      row.push(rate);
    }
    row.push(
      fmtMoney(incentiveOffer.cash || 0),
      fmtMoney(incentiveOffer.dealerCash || 0),
      filterAndJoinStrings([
        incentiveOffer.program.source,
        incentiveOffer.program.description,
      ]),
      incentiveOffer.program.groupAffiliation,
      incentiveOffer.program.previousOwnership,
      incentiveOffer.program.dealerProgramText ? (
        <button
          className="btn"
          onClick={() =>
            ProgramDetailsModal({ program: incentiveOffer.program })
          }
        >
          More Details
        </button>
      ) : (
        undefined
      ),
    );
    return row;
  };
  const rows = incentiveOffers.map(renderRow);

  return applyMore ? (
    <ApplyIncentivesMultipleTerms
      onSave={handleMultipleTermIncentives}
      financeType={financeType}
      selectedIncentives={selectedIncentiveOffers}
      offers={offers as (PurchaseOffer | LeaseOffer)[]}
    />
  ) : (
    <>
      <h2 className="modal-title">{capitalize(financeType)} incentives</h2>
      {financeType === FinanceType.LEASE && (
        <div className="incentives-show-all">
          <label>
            Show All:
            <Toggle
              on="on"
              off="off"
              height="25px"
              width="54px"
              active={showHiddenIncentives}
              onClick={(value: boolean) => setShowHiddenIncentives(value)}
            />
            {!showHiddenIncentives &&
              activeOffer &&
              (activeOffer as LeaseOffer).months && (
                <div>
                  Showing Only Tier 1 Incentives for{' '}
                  {(activeOffer as LeaseOffer).months} months
                </div>
              )}
          </label>
        </div>
      )}
      <DataTable
        className="incentives-table"
        emptyMessage="No incentives available"
        rows={rows}
        headers={getHeaders()}
      />
      <div className="modal-footer">
        {financeType !== FinanceType.CASH && (
          <button
            className="modal-button secondary cc-button-outlined"
            disabled={disabled || !selectedIncentiveOffers.length}
            onClick={() => setApplyMore(true)}
          >
            Apply to more {financeType} offers
          </button>
        )}
        <button
          key="save"
          className="modal-button save cc-button-primary"
          onClick={onSaveClicked}
          disabled={disabled}
        >
          Save
        </button>
      </div>
    </>
  );
}

export default function EditIncentives({
  financeType,
  activeOffer,
  handleClose,
  onSave,
  offers,
  car,
  deal,
  dealer,
  isOnePay,
  sendAmplitudeEvent,
  disabled = false,
}: EditIncentivesProps) {
  return (
    <Modal
      isOpen={!!activeOffer}
      onRequestClose={() => {
        if (sendAmplitudeEvent) {
          sendAmplitudeEvent(ModalType.INCENTIVES, Action.CANCEL);
        }
        handleClose();
      }}
      style={scrollableModalStyles}
    >
      <span
        className="close"
        onClick={() => {
          if (sendAmplitudeEvent) {
            sendAmplitudeEvent(ModalType.INCENTIVES, Action.CANCEL);
          }
          handleClose();
        }}
      >
        X
      </span>
      <EditIncentivesForm
        offers={offers}
        financeType={financeType}
        activeOffer={activeOffer}
        handleClose={handleClose}
        onSave={onSave}
        deal={deal}
        dealer={dealer}
        car={car}
        isOnePay={isOnePay}
        sendAmplitudeEvent={sendAmplitudeEvent}
        disabled={disabled}
      />
    </Modal>
  );
}
